import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDismiss } from './dismiss'

/**
 * Sorting and filtering a queue table, from the column headers.
 *
 * The approval and clause-review queues are worked *by fund*: somebody has the
 * one mandate open and wants that fund's rows, and the store's own order —
 * effective date, then rule key — scatters them. So the portfolio leads the
 * table and carries both controls.
 *
 * **Both controls live on the header, not in a bar above the table.** A filter
 * bar is a second place to look: the reader asks *what is this column showing*
 * while looking at the column, and has to find the answer somewhere else. On the
 * header the control is where the question is asked, the way every spreadsheet
 * this replaces already does it — and a column being filtered is legible from
 * the column itself rather than from a control the reader has to go and check.
 *
 * Sorting cycles through three states rather than two. The unsorted state is
 * not an absence: it is `lower(effective), rule_key`, which is the order the
 * oldest proposal comes first in, and a table that could only be sorted by a
 * column had no way back to it.
 */

export type Direction = 'asc' | 'desc'

export interface Sort {
  key: string
  direction: Direction
}

export function useSort(initial: Sort | null = null) {
  const [sort, setSort] = useState<Sort | null>(initial)

  function onSort(key: string) {
    setSort((was) => {
      if (was?.key !== key) return { key, direction: 'asc' }
      return was.direction === 'asc' ? { key, direction: 'desc' } : null
    })
  }

  return { sort, onSort }
}

/**
 * Sort by one named key, stably.
 *
 * `Array.prototype.sort` is stable, so rows that tie on the sort key keep the
 * store's order between them — a fund's own rules stay in effective-date order
 * underneath the fund heading, which is what somebody sorting by fund wanted.
 */
export function sortRows<T>(
  rows: T[],
  sort: Sort | null,
  keys: Record<string, (row: T) => string>,
): T[] {
  if (!sort) return rows
  const read = keys[sort.key]
  if (!read) return rows
  const sign = sort.direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => sign * read(a).localeCompare(read(b), undefined, { numeric: true }))
}

function Sorter({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string
  sortKey: string
  sort: Sort | null
  onSort: (key: string) => void
}) {
  const direction = sort?.key === sortKey ? sort.direction : null
  return (
    <button
      type="button"
      className="sorter"
      onClick={() => onSort(sortKey)}
      title={
        direction === 'asc'
          ? `Sorted by ${label}, A–Z. Click for Z–A.`
          : direction === 'desc'
            ? `Sorted by ${label}, Z–A. Click to restore the queue order.`
            : `Sort by ${label}`
      }
    >
      {label}
      <span className="arrow" aria-hidden="true">
        {direction === 'asc' ? '▲' : direction === 'desc' ? '▼' : '⇅'}
      </span>
    </button>
  )
}

function ariaSort(sort: Sort | null, sortKey: string) {
  if (sort?.key !== sortKey) return 'none' as const
  return sort.direction === 'asc' ? ('ascending' as const) : ('descending' as const)
}

export function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string
  sortKey: string
  sort: Sort | null
  onSort: (key: string) => void
  className?: string
}) {
  return (
    <th className={className} aria-sort={ariaSort(sort, sortKey)}>
      <Sorter label={label} sortKey={sortKey} sort={sort} onSort={onSort} />
    </th>
  )
}

export interface Option {
  value: string
  label: string
}

/**
 * A header that both sorts and filters its own column.
 *
 * The chosen value is echoed under the heading rather than left to the funnel's
 * shading. A filter whose only sign is a tinted icon is one somebody sets, walks
 * away from, and comes back to read as an empty queue.
 */
export function FilterHeader({
  label,
  sortKey,
  sort,
  onSort,
  value,
  options,
  onChange,
  all,
  className,
  testId,
}: {
  label: string
  sortKey: string
  sort: Sort | null
  onSort: (key: string) => void
  value: string
  options: Option[]
  onChange: (value: string) => void
  /** What the unfiltered state is called — "Every portfolio", not "All". */
  all: string
  className?: string
  testId?: string
}) {
  const { open, setOpen, box, panel, style } = useHeaderPopover()
  const chosen = options.find((o) => o.value === value)

  function pick(next: string) {
    setOpen(false)
    onChange(next)
  }

  return (
    <th className={className} aria-sort={ariaSort(sort, sortKey)}>
      <div className="th-controls">
        <Sorter label={label} sortKey={sortKey} sort={sort} onSort={onSort} />
        <div className="col-filter" ref={box}>
          <button
            type="button"
            className={value ? 'funnel on' : 'funnel'}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={value ? `Filter by ${label} — showing ${chosen?.label ?? value}` : `Filter by ${label}`}
            title={value ? `Showing ${chosen?.label ?? value} only` : `Filter by ${label}`}
            onClick={() => setOpen((was) => !was)}
          >
            <span aria-hidden="true">▽</span>
          </button>
          {open &&
            createPortal(
              <div ref={panel} className="col-filter-menu" role="menu" data-testid={testId} style={style}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={value === ''}
                  onClick={() => pick('')}
                >
                  {all}
                </button>
                {options.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={value === o.value}
                    onClick={() => pick(o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>,
              document.body,
            )}
        </div>
      </div>
      {value && (
        <span className="filtered">
          {chosen?.label ?? value}
          <button
            type="button"
            className="clear"
            aria-label={`Stop filtering by ${label}`}
            onClick={() => onChange('')}
          >
            ×
          </button>
        </span>
      )}
    </th>
  )
}

const GAP = 4
const MARGIN = 8

/**
 * A header's popover, drawn at the page under its funnel.
 *
 * Inside the header it was clipped by a table in a scrolling frame, as the row
 * menu was (RowMenu.tsx): a snapshot list three rows long cut a filter's list off
 * after its first few items. Placed from the funnel's own position, it opens below
 * it, or above where the window has no room below, and closes if the page scrolls.
 */
function useHeaderPopover() {
  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null)
  const close = useCallback(() => setOpen(false), [])
  const panel = useRef<HTMLDivElement>(null)
  const box = useDismiss<HTMLDivElement>(open, close, panel)

  useLayoutEffect(() => {
    if (!open) {
      setPlace(null)
      return
    }
    const trigger = box.current?.getBoundingClientRect()
    const list = panel.current?.getBoundingClientRect()
    if (!trigger || !list) return
    const below = trigger.bottom + GAP
    const above = trigger.top - GAP - list.height
    const top = below + list.height > window.innerHeight - MARGIN && above > MARGIN ? above : below
    const left = Math.max(MARGIN, Math.min(trigger.left, window.innerWidth - MARGIN - list.width))
    setPlace({ top, left })
  }, [open, box])

  useEffect(() => {
    if (!open) return
    const away = (event: Event) => {
      if (!panel.current?.contains(event.target as Node)) close()
    }
    window.addEventListener('scroll', away, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', away, true)
      window.removeEventListener('resize', close)
    }
  }, [open, close])

  const style = place ?? { top: 0, left: 0, visibility: 'hidden' as const }
  return { open, setOpen, box, panel, style }
}

export interface DateRange {
  /** `YYYY-MM-DD`, or empty for no bound. Both bounds are included. */
  from: string
  to: string
}

export const EVERY_DATE: DateRange = { from: '', to: '' }

export function rangeText({ from, to }: DateRange): string {
  if (from && to) return from === to ? from : `${from} – ${to}`
  return from ? `from ${from}` : `until ${to}`
}

/**
 * A header that sorts its column and narrows it to a span of days.
 *
 * A from and a to rather than a list of the days present: a list of every day a
 * snapshot was loaded runs to hundreds within the year, and the question asked of a
 * date column is "that day" or "this month", which a from and a to both answer.
 */
export function DateFilterHeader({
  label,
  sortKey,
  sort,
  onSort,
  value,
  onChange,
  all,
  className,
  testId,
}: {
  label: string
  sortKey: string
  sort: Sort | null
  onSort: (key: string) => void
  value: DateRange
  onChange: (value: DateRange) => void
  /** What the unfiltered state is called — "Every as-at date", not "All". */
  all: string
  className?: string
  testId?: string
}) {
  const { open, setOpen, box, panel, style } = useHeaderPopover()
  const on = Boolean(value.from || value.to)
  const said = on ? rangeText(value) : ''

  return (
    <th className={className} aria-sort={ariaSort(sort, sortKey)}>
      <div className="th-controls">
        <Sorter label={label} sortKey={sortKey} sort={sort} onSort={onSort} />
        <div className="col-filter" ref={box}>
          <button
            type="button"
            className={on ? 'funnel on' : 'funnel'}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={on ? `Filter by ${label} — showing ${said}` : `Filter by ${label}`}
            title={on ? `Showing ${said} only` : `Filter by ${label}`}
            onClick={() => setOpen((was) => !was)}
          >
            <span aria-hidden="true">▽</span>
          </button>
          {open &&
            createPortal(
              <div
                ref={panel}
                className="col-filter-menu date-range"
                role="dialog"
                aria-label={`Filter by ${label}`}
                data-testid={testId}
                style={style}
              >
                <label>
                  From
                  <input
                    type="date"
                    value={value.from}
                    max={value.to || undefined}
                    onChange={(e) => onChange({ ...value, from: e.target.value })}
                  />
                </label>
                <label>
                  To
                  <input
                    type="date"
                    value={value.to}
                    min={value.from || undefined}
                    onChange={(e) => onChange({ ...value, to: e.target.value })}
                  />
                </label>
                <button
                  type="button"
                  disabled={!on}
                  onClick={() => {
                    setOpen(false)
                    onChange(EVERY_DATE)
                  }}
                >
                  {all}
                </button>
              </div>,
              document.body,
            )}
        </div>
      </div>
      {on && (
        <span className="filtered">
          {said}
          <button
            type="button"
            className="clear"
            aria-label={`Stop filtering by ${label}`}
            onClick={() => onChange(EVERY_DATE)}
          >
            ×
          </button>
        </span>
      )}
    </th>
  )
}

/** How many of how many, said plainly. A filtered table that only shows the
 *  filtered count leaves the reader unsure whether the rest went away or were
 *  never there. */
export function Showing({
  shown,
  total,
  noun,
  onClear,
}: {
  shown: number
  total: number
  noun: string
  onClear?: () => void
}) {
  if (shown === total) return null
  return (
    <span className="showing" data-testid="showing">
      Showing {shown} of {total} {noun}
      {onClear && (
        <button type="button" className="link" onClick={onClear}>
          Clear filters
        </button>
      )}
    </span>
  )
}
